/**
 * The networked session. [P3-*] client half
 *
 * Mirrors `LocalSession`'s shape exactly, so `app.ts`, `PlacementController` and `MeeplePicker` do not
 * care which one they are holding. The difference is only in *when* state changes: locally an action
 * applies immediately; remotely it goes to the server and the new state arrives on the next broadcast.
 *
 * State is never serialised across the wire. The server sends `(seed, options, actions[])` and this
 * replays it through the same pure engine the server used — see the header of `@carc/protocol`. The
 * checksum that rides along is compared on every update, so a divergence is loud rather than a slow
 * drift nobody notices until the scores are wrong.
 */

import {
  applyAll,
  buildTileIndex,
  createGame,
  legalMeepleTargets as engineLegalMeepleTargets,
  legalPlacements as engineLegalPlacements,
  type GameAction,
  type GameEvent,
  type GameState,
  type Result,
  type Rotation,
  type TileIndex,
} from '@carc/engine';
import {
  checksumState,
  SEAT_COLOURS,
  type ClientMessage,
  type RoomView,
  type ServerMessage,
} from '@carc/protocol';
import type { SeatSetup, SessionListener } from './session.js';

const seatColourHex = (id: string): string =>
  SEAT_COLOURS.find((c) => c.id === id)?.hex ?? '#4a4e69';

export interface RemoteEvents {
  onRoom?: (room: RoomView) => void;
  onError?: (message: string) => void;
  onStarted?: () => void;
  onConnectionChange?: (connected: boolean) => void;
}

export class RemoteSession {
  readonly tiles: TileIndex = buildTileIndex();
  readonly actions: GameAction[] = [];

  private ws: WebSocket | null = null;
  private listeners = new Set<SessionListener>();
  private current: GameState | null = null;
  private room: RoomView | null = null;
  private token: string | null = null;
  private joinedCode: string | null = null;
  private playerName = 'Player';
  private reconnectDelay = 500;
  private closedByUs = false;
  /** True until the first snapshot has been rendered, so we can skip the animation backlog. */
  private priming = true;
  /**
   * Set by the app to display the server's turn clock. [P6-timer]
   *
   * The server has always broadcast this and nothing has ever listened, which is half of why the timer
   * appeared not to exist. Display only — expiry is the server's to enforce, and a client that also
   * enforced it would race the authority it is supposed to be following.
   */
  onTurnDeadline: ((msRemaining: number, seat: number) => void) | null = null;

  constructor(private readonly hooks: RemoteEvents = {}) {}

  // ── connection ─────────────────────────────────────────────────────────────────────────────────

  connect(): Promise<void> {
    const scheme = location.protocol === 'https:' ? 'wss' : 'ws';
    const url = `${scheme}://${location.host}/ws`;
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(url);
      this.ws = ws;
      ws.onopen = () => {
        this.reconnectDelay = 500;
        this.hooks.onConnectionChange?.(true);
        resolve();
      };
      ws.onerror = () => reject(new Error('Could not reach the game server.'));
      ws.onmessage = (ev) => this.receive(JSON.parse(String(ev.data)) as ServerMessage);
      ws.onclose = () => {
        this.hooks.onConnectionChange?.(false);
        if (!this.closedByUs) this.scheduleReconnect();
      };
    });
  }

  private scheduleReconnect(): void {
    const delay = Math.min(this.reconnectDelay, 10_000);
    this.reconnectDelay = delay * 2;
    setTimeout(() => {
      void this.connect()
        .then(() => {
          // Rejoin with the seat token so we get our seats back rather than arriving as a stranger.
          if (this.joinedCode) {
            this.send({
              type: 'join_room',
              code: this.joinedCode,
              name: this.playerName,
              ...(this.token ? { token: this.token } : {}),
            });
          }
        })
        .catch(() => this.scheduleReconnect());
    }, delay);
  }

  disconnect(): void {
    this.closedByUs = true;
    this.ws?.close();
  }

  private send(msg: ClientMessage): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  // ── inbound ────────────────────────────────────────────────────────────────────────────────────

  private receive(msg: ServerMessage): void {
    switch (msg.type) {
      case 'welcome':
        this.token = msg.token;
        return;

      case 'room':
        this.room = msg.room;
        this.joinedCode = msg.room.code;
        this.hooks.onRoom?.(msg.room);
        return;

      case 'game': {
        this.room = msg.room;
        this.joinedCode = msg.room.code;
        const wasPriming = this.priming;

        const rebuilt = createGame({
          seed: msg.sync.seed,
          seatCount: msg.sync.seatCount,
          stackCount: msg.sync.options.stackCount,
        });
        const replayed = applyAll(rebuilt, msg.sync.actions, this.tiles);
        if (!replayed.ok) {
          this.hooks.onError?.('Lost sync with the game — asking the server to resend.');
          this.send({ type: 'resync' });
          return;
        }

        const state = replayed.value.state;
        if (checksumState(state) !== msg.sync.checksum) {
          // Loud on purpose. If this ever fires, client and server disagree about the rules, and
          // silently carrying on would corrupt the game rather than fail it.
          console.error('[carcassonne] checksum mismatch — client and server disagree');
          this.hooks.onError?.('Out of sync with the server. Reloading the board.');
        }

        this.actions.length = 0;
        this.actions.push(...msg.sync.actions);
        this.current = state;

        if (wasPriming) {
          this.priming = false;
          this.hooks.onStarted?.();
          // No animation backlog on the first snapshot — a player joining a game in progress (or
          // reconnecting) should land on the current board, not watch a hundred replayed moves.
          this.emit(state, []);
        } else {
          this.emit(state, msg.events ?? []);
        }
        return;
      }

      case 'turn_deadline':
        this.onTurnDeadline?.(msg.msRemaining, msg.seat);
        return;

      case 'rule_violation':
      case 'error':
        this.hooks.onError?.(msg.message);
        return;

      default:
        return;
    }
  }

  private emit(state: GameState, events: readonly GameEvent[]): void {
    for (const listener of this.listeners) listener(state, events);
  }

  // ── lobby actions ──────────────────────────────────────────────────────────────────────────────

  createRoom(name: string, seatCount: number, options: { stackCount: number; turnSeconds: number }): void {
    this.playerName = name;
    this.send({
      type: 'create_room',
      name,
      seatCount,
      options: { ...options, packs: ['base'] },
    });
  }

  joinRoom(code: string, name: string): void {
    this.playerName = name;
    this.send({ type: 'join_room', code: code.toUpperCase(), name, ...(this.token ? { token: this.token } : {}) });
  }

  claimSeat(seat: number, name?: string): void {
    this.send({ type: 'claim_seat', seat, ...(name ? { name } : {}) });
  }

  releaseSeat(seat: number): void {
    this.send({ type: 'release_seat', seat });
  }

  setSeatAi(seat: number, ai: boolean, difficulty: 'easy' | 'steady' | 'sharp' = 'steady'): void {
    this.send({ type: 'set_seat_ai', seat, ai, ...(ai ? { difficulty } : {}) });
  }

  startGame(): void {
    this.send({ type: 'start_game' });
  }

  // ── the LocalSession-shaped surface app.ts consumes ────────────────────────────────────────────

  get isRemote(): boolean {
    return true;
  }

  get roomView(): RoomView | null {
    return this.room;
  }

  get roomCode(): string {
    return this.room?.code ?? '';
  }

  /** Which seats THIS device controls. One connection may hold several — that is what makes hybrid work. */
  get mySeats(): readonly number[] {
    return (this.room?.seats ?? []).filter((s) => s.mine).map((s) => s.index);
  }

  get seats(): readonly SeatSetup[] {
    return (this.room?.seats ?? []).map((s) => ({
      name: s.name || `Seat ${s.index + 1}`,
      colour: seatColourHex(s.colour),
    }));
  }

  get state(): GameState {
    if (!this.current) throw new Error('No game state yet — the game has not started.');
    return this.current;
  }

  get hasState(): boolean {
    return this.current !== null;
  }

  onChange(listener: SessionListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  seatFor(index: number): SeatSetup {
    return this.seats[index] ?? { name: `Seat ${index + 1}`, colour: '#999' };
  }

  legalPlacementsForCurrentTile(): { pos: { x: number; y: number }; rotation: Rotation }[] {
    if (!this.current?.currentTile) return [];
    return engineLegalPlacements(this.current, this.tiles, this.current.currentTile.defId);
  }

  legalMeepleTargets(): number[] {
    if (!this.current) return [];
    return engineLegalMeepleTargets(this.current, this.tiles);
  }

  // Action methods. These dispatch to the server and return the state we currently hold; the real
  // result arrives as a broadcast. The signature matches LocalSession so callers stay identical.
  draw(stack?: number): Result<{ state: GameState; events: readonly GameEvent[] }> {
    return this.dispatch({ type: 'draw', seat: this.activeSeat(), ...(stack !== undefined ? { stack } : {}) });
  }

  placeTile(pos: { x: number; y: number }, rotation: Rotation) {
    return this.dispatch({ type: 'place_tile', seat: this.activeSeat(), pos, rotation });
  }

  placeMeeple(featureIndex: number) {
    return this.dispatch({ type: 'place_meeple', seat: this.activeSeat(), featureIndex });
  }

  skipMeeple() {
    return this.dispatch({ type: 'skip_meeple', seat: this.activeSeat() });
  }

  private activeSeat(): number {
    return this.current?.activeSeat ?? 0;
  }

  private dispatch(action: GameAction): Result<{ state: GameState; events: readonly GameEvent[] }> {
    this.send({ type: 'action', action: action as never });
    return { ok: true, value: { state: this.state, events: [] } };
  }
}
